import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { GraduationCap, X } from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
const resolveUrl = (u) => (u && u.startsWith('http') ? u : `${BACKEND}${u || ''}`);

/**
 * Tutorials button + modal (Session 36, P3 item h).
 * Shows admin-uploaded tutorial videos grouped by Hub area to help new users
 * learn how to navigate each tool. Renders nothing when no tutorials exist.
 */
export default function TutorialsButton({ className = '' }) {
  const token = localStorage.getItem('token');
  const [videos, setVideos] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    axios.get(`${API}/help-videos`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setVideos(res.data.videos || []))
      .catch(() => setVideos([]));
  }, []);

  if (!videos.length) return null;

  // group by area_label
  const groups = videos.reduce((acc, v) => {
    (acc[v.area_label] = acc[v.area_label] || []).push(v);
    return acc;
  }, {});

  return (
    <>
      <Button
        variant="outline"
        className={`border-cyan-500/40 text-cyan-700 hover:bg-cyan-50 ${className}`}
        onClick={() => setOpen(true)}
        data-testid="open-tutorials-btn"
      >
        <GraduationCap className="w-4 h-4 mr-2" /> Tutorials
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="tutorials-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GraduationCap className="w-5 h-5 text-cyan-600" /> Tutorials &amp; How-Tos
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-6 mt-2">
            {Object.entries(groups).map(([area, list]) => (
              <div key={area}>
                <h4 className="text-sm font-semibold text-gray-700 mb-2">{area}</h4>
                <div className="grid sm:grid-cols-2 gap-4">
                  {list.map((v) => (
                    <div key={v.id} className="rounded-lg border border-gray-200 overflow-hidden" data-testid={`tutorial-video-${v.id}`}>
                      <video
                        src={resolveUrl(v.video_url)}
                        controls
                        preload="metadata"
                        className="w-full bg-black aspect-video"
                      />
                      <div className="p-3">
                        <p className="font-medium text-gray-800 text-sm">{v.title}</p>
                        {v.description && <p className="text-xs text-gray-500 mt-1">{v.description}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
